import React, {createContext, useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import MembershipService from '@tkww/orion-web-legacy-union--sdk-membership';
import analytics from '@tkww/orion-web-legacy-union--tk-analytics';
import {useUnleashContext} from '@unleash/proxy-client-react';
import Honeybadger from '@honeybadger-io/js';
import affiliateAnalytics from '@xo-union/affiliate-marketing-analytics';
import sha1 from 'crypto-js/sha1';
import {
  useGetMagentoToken,
  MAGENTO_CUSTOMER_TOKEN_KEY,
} from '@tkww/reg-shared-util-get-magento-token';
import {REGISTRY_LANDING_PAGE_URL} from '../constants';
import getMemberToken from '../utils/getMemberToken';
import {obfuscate} from '../utils/loggingUtils';
import removeTKCookie from '../utils/removeTKCookie';
import WeddingsApi from '../services/WeddingsApi';

export const membership = new MembershipService({
  clientOptions: {version: 'v2'},
});

const defaults = {
  member: {},
  homeAddress: {},
  isFetching: true,
  hasError: false,
};

const Context = createContext({
  ...defaults,
});

export default Context;

const getMemberIdentifyArgs = ({
  id,
  legacy_user_id: legacyUserId,
  email,
  first_name: firstName,
  last_name: lastName,
  fiance_first_name: fianceFirstName,
  fiance_last_name: fianceLastName,
  number_of_guests: numberOfGuests,
}) => [
  legacyUserId,
  {
    email,
    firstName,
    lastName,
    fianceFirstName,
    fianceLastName,
    memberId: id,
    weddingSize: numberOfGuests?.replace('–', '-'),
  },
];

export const MemberContextProvider = ({children}) => {
  const updateContext = useUnleashContext();
  const [member, setMember] = useState(defaults.member);
  const [homeAddress, setHomeAddress] = useState(defaults.homeAddress);
  const [isFetching, setIsFetching] = useState(true);
  const [hasError, setHasError] = useState(false);

  const sessionToken = getMemberToken();
  const {data: magentoToken, error: magentoTokenError} = useGetMagentoToken();

  const clearMember = () => {
    Honeybadger.clear();

    removeTKCookie('xo-session-token');
    removeTKCookie('cart_id');

    localStorage.removeItem(MAGENTO_CUSTOMER_TOKEN_KEY);

    setMember(defaults.member);
    setHomeAddress(defaults.homeAddress);
  };

  const logOut = () => {
    clearMember();
    membership.logOut();
    window.location.replace(REGISTRY_LANDING_PAGE_URL);
  };

  const handleMemberValidationError = () => {
    clearMember();
    setIsFetching(false);
    setHasError(true);
  };

  const updateMemberGuestCount = newGuestCount => {
    if (!newGuestCount) return;

    setMember({
      ...member,
      number_of_guests: newGuestCount,
    });
  };

  useEffect(() => {
    (async function fetchMember() {
      if (!sessionToken) {
        handleMemberValidationError();
        return;
      }

      try {
        const {
          data: {member: theMember},
        } = await membership.getMember({
          token: sessionToken,
        });

        if (theMember) {
          const weddingResponse = await WeddingsApi.getWedding(theMember.id);

          theMember.wedding_date = weddingResponse?.wedding?.wedding_date;
          theMember.number_of_guests = weddingResponse?.wedding?.guest_range;

          try {
            await updateContext({properties: {userEmail: theMember.email}});
          } catch (error) {
            Honeybadger.notify(error, 'UnleashContextUpdateError');
          }
          setMember(theMember);

          Honeybadger.setContext({
            memberId: theMember.id,
            membershipSessionToken: obfuscate(sessionToken),
            user_id: theMember.id,
          });

          if (theMember.addresses) {
            setHomeAddress(
              theMember.addresses.find(a => a.address_type === 'home') || {},
            );
          }
        } else {
          handleMemberValidationError();
          return;
        }
      } catch (error) {
        handleMemberValidationError();
        return;
      }

      setIsFetching(false);
    })();
  }, []); // eslint-disable-line

  useEffect(() => {
    if (member.id) {
      analytics.identify(...getMemberIdentifyArgs(member));
      affiliateAnalytics.identify({
        customerId: member.id,
        customerEmail: sha1(member.email),
      });
    }
  }, [member]);

  useEffect(() => {
    if (!magentoTokenError) return;

    Honeybadger.notify(magentoTokenError, 'MagentoCustomerTokenFetchError');
  }, [magentoTokenError]);

  return (
    <Context.Provider
      value={{
        member,
        memberId: member.id,
        updateMemberGuestCount,
        homeAddress,
        logOut,
        magentoToken,
        isFetching,
        hasError,
      }}>
      {children}
    </Context.Provider>
  );
};

MemberContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
